import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Plus, SignIn, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

export default function Teams() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login?next=/teams"); return; }
    api.get("/teams/me").then(({ data }) => setItems(data.items || [])).finally(() => setFetching(false));
  }, [user, loading, navigate]);

  if (loading) return <PageShell title="Loading…" />;

  return (
    <PageShell eyebrow={`Hi, ${user?.name?.split(" ")[0] || "there"}`} title="Your teams." subtitle="Create, join, and switch between team workspaces. Each has its own 1 GB shared repo.">
      <PageMeta
        title="Team workspaces · Shared file repos with 6-digit codes"
        description="Create a team workspace on SendMe — 1 GB shared storage, up to 10 members, nested folders, activity log. Members join with a Team ID + password, no signup."
      />      <div className="flex flex-wrap gap-3 mb-8">
        <Link to="/teams/create" className="btn-brut-primary" data-testid="create-team-cta">
          <Plus size={16} weight="bold"/> Create team
        </Link>
        <Link to="/teams/join" className="btn-brut" data-testid="join-team-cta">
          <SignIn size={16}/> Join team
        </Link>
      </div>

      {fetching ? (
        <div className="border border-dashed border-black/20 p-12 text-center text-black/50">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-black/20 p-16 text-center" data-testid="teams-empty">
          <Users size={42} className="mx-auto mb-3 text-[#FF3366]" weight="duotone"/>
          <div className="text-2xl font-bold mb-2">No teams yet</div>
          <p className="text-black/60 mb-6">Create one to start sharing files with up to 10 people, or join an existing team.</p>
          <div className="inline-flex gap-3">
            <Link to="/teams/create" className="btn-brut-primary">Create team</Link>
            <Link to="/teams/join" className="btn-brut">Join team</Link>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="teams-list">
          {items.map((t) => {
            const pct = Math.min(100, Math.round((t.storage_used / t.storage_quota) * 100));
            return (
              <Link key={t.team_id} to={`/teams/${t.team_id}`} className="brut-card p-5 brut-hover flex flex-col gap-3" data-testid={`team-card-${t.team_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="small-caps mb-1">{t.is_leader ? "Leader" : "Member"}{t.status === "suspended" && " · Suspended"}</div>
                    <div className="text-xl font-bold tracking-tight">{t.name}</div>
                    <div className="text-xs font-mono text-black/55 mt-1">ID · {t.team_id}</div>
                  </div>
                  <ArrowRight size={18}/>
                </div>
                <div className="flex items-center justify-between text-xs font-mono">
                  <span>{t.member_count} member{t.member_count > 1 ? "s" : ""}</span>
                  <span>{fmtBytes(t.storage_used)} / {fmtBytes(t.storage_quota)}</span>
                </div>
                <div className="h-1 bg-black/10">
                  <div className="h-1 bg-[#FF3366]" style={{ width: `${pct}%` }}/>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
